import { useState, useContext, useMemo } from 'react'

import LootHistoryTable from './LootHistoryTable'
import LootStatisticsTable from './LootStatisticsTable'
import { LootContext } from '../context'
import { useTeamLootHistory } from 'app/frontend/base/config/queries'
import { useTeam } from 'app/frontend/base/config/context/team'
import ItemSlot, {
  itemSlotLabels,
  itemSlots,
} from 'app/frontend/types/custom/ItemSlot'
import OptionBar from './OptionBar'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import DifficultySelector from './DifficultySelector'
import Select from 'app/frontend/base/components/Select'
import { DateRangePicker } from '@/components/ui/date-range-picker'
import { DateRange } from 'react-day-picker'
import NoMemberWarning from './NoMemberWarning'
import { difficultiesLabels } from 'app/frontend/types/custom/Difficulty'

const LootHistoryOverview = () => {
  const { team } = useTeam()
  const [customRange, setCustomRange] = useState<DateRange>()
  const [slot, setSlot] = useState<ItemSlot | 'all'>('all')
  const [allDifficulties, setAllDifficulties] = useState(false)
  const { difficulty } = useContext(LootContext)

  const { selectedSeason } = useContext(LootContext)

  const historyOptions = {
    teamId: team.id,
    difficulty: allDifficulties ? undefined : difficulty,
    slot: slot === 'all' ? undefined : slot,
    seasonId: selectedSeason.id,
    customRange,
  }

  const { data, isPending } = useTeamLootHistory(historyOptions)

  const visibleMembers = useMemo(() => {
    return (
      team.members?.filter((member) => member.teamRank?.wishlist_visibility) ??
      []
    )
  }, [team.members])

  return (
    <div className='wishlists loot-history'>
      <h2 className='flex justify-between items-center gap-4 flex-wrap'>
        <div className='flex items-center whitespace-nowrap'>Loot history</div>
        <OptionBar
          disabled={{ valueDisplay: true, season: !!customRange }}
          onSeasonChange={() => setCustomRange(undefined)}
        >
          <DateRangePicker
            date={customRange}
            setDate={setCustomRange}
            isClearable={true}
          />
        </OptionBar>
      </h2>

      <div className='max-w-200'>
        <p>
          This page displays all awarded items found in the loot history of the
          RCLootCouncil addon. If you want to update the data, install and run
          the{' '}
          <a
            href='/desktop'
            target='_blank'
            rel='noreferrer'
            className='text-primary'
          >
            desktop client
          </a>
          .
        </p>
      </div>

      {visibleMembers.length === 0 ? (
        <NoMemberWarning className='mt-6' section='loot' />
      ) : (
        <>
          <div className='mt-8 mb-2 flex flex-wrap gap-2 justify-start'>
            <DifficultySelector
              setToAll={setAllDifficulties}
              setAsAll={allDifficulties}
            />

            <div className='flex flex-col gap-1'>
              <span className='font-semibold'>Slot</span>
              <Select
                options={(
                  itemSlots.map((slot) => ({
                    value: slot,
                    label: itemSlotLabels[slot],
                  })) as { value: string; label: string }[]
                ).concat([{ value: 'tier', label: 'Tier' }])}
                value={slot === 'all' ? undefined : slot}
                placeholder='All slots'
                isClearable={true}
                className='w-40 h-[2.6rem]'
                setValue={(value) =>
                  value ? setSlot(value as ItemSlot) : setSlot('all')
                }
              />
            </div>
          </div>

          {isPending || (data?.history_items.length || 0) > 0 ? (
            <>
              <LootStatisticsTable historyOptions={historyOptions} />

              <LootHistoryTable
                historyOptions={historyOptions}
                allDifficulties={allDifficulties}
              />
            </>
          ) : data?.has_any_history ? (
            <p className='mt-6 text-sm text-muted-foreground'>
              No loot history items found in{' '}
              {customRange ? 'this time range' : selectedSeason.name}
              {team.settings.wishlistDifficulties.value.length > 1 &&
              !allDifficulties
                ? ` on ${difficultiesLabels[difficulty]}`
                : ''}
            </p>
          ) : (
            <Alert className='mt-6'>
              <AlertTitle>No loot history data found</AlertTitle>
              <AlertDescription>
                Make sure you have the{' '}
                <a href='/desktop' target='_blank' rel='noreferrer'>
                  wowaudit desktop client
                </a>{' '}
                installed and running.
              </AlertDescription>
            </Alert>
          )}
        </>
      )}
    </div>
  )
}

export default LootHistoryOverview
